import { useCallback, useEffect } from 'react';
import { graphql } from 'react-relay';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop, useQueryInterop } from '@confluence/apollo-relay-interop';
import { createEditorContentUnifiedQueryVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';

import type {
	useEmojiEverSetPropertyQuery$data,
	useEmojiEverSetPropertyQuery$variables,
} from './__generated__/useEmojiEverSetPropertyQuery.graphql.ts';
import type {
	useEmojiEverSetPropertyMutation$data,
	useEmojiEverSetPropertyMutation$variables,
} from './__generated__/useEmojiEverSetPropertyMutation.graphql.ts';

const EMOJI_EVER_SET_PROPERTY_KEY = 'emoji-ever-set';

type Property =
	| {
			readonly id: string | null | undefined;
			readonly key: string | null | undefined;
			readonly value: string | null | undefined;
	  }
	| null
	| undefined;

type EmojiEverSetProperties = {
	hasEverSetEmoji: boolean;
	setEmojiEverSet: (value?: boolean) => Promise<boolean>;
	loading: boolean;
	error: any;
	updating: boolean;
};

type UseEmojiEverSetPropertyOptions = {
	pageAri: string | null;
	contentId: string;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	contentType?: string;
	isLivePage: boolean;
	isAutoPageEmojiEligible?: boolean;
};
export function useEmojiEverSetProperty({
	pageAri,
	contentId,
	createAnalyticsEvent,
	contentType,
	isLivePage,
	isAutoPageEmojiEligible,
}: UseEmojiEverSetPropertyOptions): EmojiEverSetProperties {
	const { data, error, loading, refetch } = useQueryInterop<
		useEmojiEverSetPropertyQuery$data,
		useEmojiEverSetPropertyQuery$variables
	>(
		/** @schema cc */
		graphql`
			query useEmojiEverSetPropertyQuery($contentId: ID, $status: [String], $draftShareId: String) {
				content(id: $contentId, draftShareId: $draftShareId, status: $status) {
					nodes {
						id
						properties(key: "emoji-ever-set") {
							nodes {
								id
								key
								value
							}
						}
					}
				}
			}
		`,
		{
			variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
				? createEditorContentUnifiedQueryVariables({
						contentId,
						draftShareId: getDraftShareIdFromQueryParam(),
					})
				: {
						contentId,
						status: ['current', 'draft'],
						draftShareId: getDraftShareIdFromQueryParam(),
					},
			fetchPolicy: 'cache-and-network',
		},
	);

	const [createPropertyMutation, { loading: updating }] = useMutationInterop<
		useEmojiEverSetPropertyMutation$data,
		useEmojiEverSetPropertyMutation$variables
	>(
		/** @schema agg */ graphql`
			mutation useEmojiEverSetPropertyMutation($input: ConfluenceCreatePagePropertyInput!) {
				confluence {
					createPageProperty(input: $input) {
						success
						pageProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const setEmojiEverSet = useCallback(
		async (value: boolean = true) => {
			if (!contentId || !pageAri) {
				return false;
			}

			try {
				const result = await createPropertyMutation({
					variables: {
						input: {
							pageId: pageAri,
							key: EMOJI_EVER_SET_PROPERTY_KEY,
							value: String(value),
						},
					},
				});
				const success = result?.data?.confluence?.createPageProperty?.success;

				if (success) {
					void refetch();

					if (createAnalyticsEvent && isAutoPageEmojiEligible) {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'success',
								actionSubject: 'updateEmojiEverSetProperty',
								source: 'useEmojiEverSetProperty',
								attributes: {
									contentType,
									isLivePage,
								},
							},
						}).fire();
					}
				}
				return success || false;
			} catch {
				if (createAnalyticsEvent && isAutoPageEmojiEligible) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'errored',
							actionSubject: 'updateEmojiEverSetProperty',
							source: 'useEmojiEverSetProperty',
							attributes: {
								contentType,
								isLivePage,
							},
						},
					}).fire();
				}
				return false;
			}
		},
		[
			contentId,
			pageAri,
			createPropertyMutation,
			refetch,
			createAnalyticsEvent,
			contentType,
			isLivePage,
			isAutoPageEmojiEligible,
		],
	);

	useEffect(() => {
		if (data && createAnalyticsEvent && isAutoPageEmojiEligible) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'success',
					actionSubject: 'emojiEverSetProperty',
					source: 'useEmojiEverSetProperty',
					attributes: {
						contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	}, [data, createAnalyticsEvent, contentType, isLivePage, isAutoPageEmojiEligible]);

	useEffect(() => {
		if (error && createAnalyticsEvent && isAutoPageEmojiEligible) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'errored',
					actionSubject: 'emojiEverSetProperty',
					source: 'useEmojiEverSetProperty',
					attributes: {
						contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent, contentType, isLivePage, isAutoPageEmojiEligible]);

	if (!contentId || !pageAri) {
		// By default, we don't want to set the emoji if we don't know the result so hasEverSetEmoji is true.
		return {
			hasEverSetEmoji: true,
			setEmojiEverSet: () => Promise.resolve(false),
			loading: false,
			error: null,
			updating: false,
		};
	}

	const currentValue = data?.content?.nodes?.[0]?.properties?.nodes?.find(
		(prop: Property) => prop?.key === EMOJI_EVER_SET_PROPERTY_KEY,
	)?.value;

	const hasEverSetEmoji = currentValue === 'true';

	return {
		hasEverSetEmoji,
		setEmojiEverSet,
		loading,
		error,
		updating,
	};
}
